import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Home,
  Bluetooth,
  ScanLine,
  History,
  Info,
  User,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Languages,
  Cpu,
} from 'lucide-react'
import { useMemo } from 'react'
import { RfidMobileSidebar } from '@/components/layout/RfidMobileSidebar'
import { RfidDrawerProvider, useRfidDrawer } from '@/contexts/RfidDrawerContext'
import { useAuthStore } from '@/stores/authStore'
import { useLocale } from '@/i18n/LocaleProvider'
import { displayUserName, cn } from '@/utils/cn'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { canAccessRfidEngineAdmin } from '@/utils/rfidAccess'
import logo from '@/assets/logo.png'

export default function AppLayout() {
  return (
    <RfidDrawerProvider collapseStorageKey="rfid-re-fe.sidebarCollapsed">
      <AppLayoutInner />
    </RfidDrawerProvider>
  )
}

function AppLayoutInner() {
  const { user, logout } = useAuthStore()
  const { t, locale, toggleLocale } = useLocale()
  const navigate = useNavigate()
  const { sidebarCollapsed: collapsed, toggleSidebarCollapsed } = useRfidDrawer()
  const showEngine = canAccessRfidEngineAdmin(user)

  const NAV = useMemo(() => {
    const items = [
      { to: '/', label: t.App.home, icon: Home, end: true },
      { to: '/scanner', label: t.App.scanner, icon: Bluetooth },
      { to: '/raw-scan', label: t.App.rawScan, icon: ScanLine },
      { to: '/recent-scans', label: t.App.recentScans, icon: History },
      { to: '/scanner/info', label: t.App.scannerInfo, icon: Info },
      { to: '/profile', label: t.App.profile, icon: User },
    ]
    if (showEngine) {
      items.splice(5, 0, {
        to: '/engine',
        label: t.App.engine ?? 'Engine',
        icon: Cpu,
        end: false,
      })
    }
    return items
  }, [t, showEngine])

  const onLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="relative h-full overflow-hidden bg-background">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-200 lg:flex',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        <div
          className={cn(
            'rfid-sidebar-brand px-3',
            collapsed ? 'justify-center' : 'gap-3 px-4'
          )}
        >
          <img
            src={logo}
            alt="LEAP"
            className={cn('brightness-0 invert', collapsed ? 'h-7' : 'h-8')}
          />
          {!collapsed ? (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{t.App.name}</div>
              <div className="truncate text-[10px] uppercase tracking-wider text-white/50">
                {t.App.subtitle}
              </div>
            </div>
          ) : null}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              title={label}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg border-2 px-2.5 py-2 text-sm transition-colors',
                  collapsed && 'justify-center px-2',
                  isActive
                    ? 'border-white bg-black text-white shadow-sm'
                    : 'border-transparent text-white/90 hover:bg-black/20'
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed ? <span className="truncate">{label}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-sidebar-border p-2">
          <button
            type="button"
            className="hidden w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs text-white/80 hover:bg-black/20 lg:flex"
            onClick={toggleSidebarCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {!collapsed ? 'Collapse' : null}
          </button>

          {!collapsed ? (
            <ThemeSwitcher variant="sidebar" />
          ) : (
            <div className="flex justify-center">
              <ThemeSwitcher
                variant="icon"
                className="!h-9 !w-9 border-white/15 bg-black/20 text-white hover:bg-black/30"
              />
            </div>
          )}

          <button
            type="button"
            onClick={toggleLocale}
            title={t.App.language}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 hover:bg-black/20',
              collapsed && 'justify-center px-2'
            )}
          >
            <Languages className="h-4 w-4 shrink-0" />
            {!collapsed ? <span>{locale === 'en' ? 'العربية' : 'English'}</span> : null}
          </button>

          {!collapsed ? (
            <div className="truncate px-2 text-xs text-white/60">{displayUserName(user)}</div>
          ) : null}

          <button
            type="button"
            onClick={onLogout}
            title={t.App.signOut}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 hover:bg-black/20',
              collapsed && 'justify-center px-2'
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed ? t.App.signOut : null}
          </button>
        </div>
      </aside>

      <RfidMobileSidebar />

      <div
        className={cn(
          'flex h-full min-h-0 min-w-0 flex-col transition-[margin] duration-200',
          collapsed ? 'lg:ml-16' : 'lg:ml-60'
        )}
      >
        <main className="rfid-app-canvas flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
